import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from './init';
import type { ProjectFile } from './schema';
import { getCurrentRole, isStaffRole } from './roles';

/**
 * A per-tab id, generated once when this module loads. Every save stamps the project doc with it
 * (`lastEditedSession`) so the optimistic-concurrency guard can tell THIS tab's own earlier save
 * (resave freely) from a save made in another tab or by another user (genuinely "saved from another
 * window" → reject). This is necessary because the client SDK cannot reliably read back its own
 * just-committed `serverTimestamp()`, so a plain `updatedAt` comparison always flags the second save
 * in a single tab as stale. In-memory only: a reload starts a new session, which is the right
 * granularity (a reloaded tab has re-read the doc and holds a fresh token).
 */
function makeSessionId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
const CLIENT_SESSION_ID = makeSessionId();

/** A typed error carrying a `code` the action layer maps to a UI modal. */
class ProjectError extends Error {
  code: 'stale' | 'forbidden' | 'not-found' | 'unauthenticated';

  constructor(code: ProjectError['code'], message: string) {
    super(message);
    this.code = code;
  }
}

/** Strip UI-only fields; keep only the contract shape. */
function toStoredFile(file: any): ProjectFile {
  const stored: ProjectFile = {
    id: file.id,
    name: file.name,
    content: file.content ?? '',
    fileType: file.fileType,
    children: file.children ?? []
  };
  if (file.url) stored.url = file.url;
  if (file.isSelectedFile) stored.isSelectedFile = true;
  return stored;
}

/** Re-derive editor-compat fields on read (`_id` mirrors `id`). */
function fromStoredFile(file: ProjectFile): any {
  return { ...file, _id: file.id };
}

function tsToMillis(value: unknown): number {
  if (value instanceof Timestamp) return value.toMillis();
  if (typeof value === 'number') return value;
  return 0;
}

/** Map a stored project doc to the shape the editor's reducers expect (mirrors the old REST payload). */
function toEditorProject(id: string, data: any) {
  return {
    id,
    name: data.name,
    visibility: data.visibility,
    editableByLink: data.editableByLink ?? false,
    files: (data.files || []).map(fromStoredFile),
    createdAt: tsToMillis(data.createdAt),
    updatedAt: tsToMillis(data.updatedAt),
    // uid of whoever last wrote the doc; lets the live listener tell a foreign (instructor) edit
    // from the reader's own save echo. Absent on legacy docs written before this field existed.
    lastEditedBy: data.lastEditedBy ?? null,
    // Per-project AR live-bridge session token; absent on projects never opened in AR.
    // Carried so the editor reuses the same session instead of minting a new one each press.
    arSessionId: data.arSessionId ?? null,
    user: { id: data.userId, username: data.ownerUsername }
  };
}

function requireUid(): string {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new ProjectError('unauthenticated', 'Not authenticated.');
  return uid;
}

/** Create a new sketch. Id is client-generated; owner username is read from the profile doc. */
export async function createProject(input: {
  name: string;
  files: any[];
  visibility?: 'Public' | 'Private';
}) {
  const uid = requireUid();
  const userSnap = await getDoc(doc(db, `users/${uid}`));
  if (!userSnap.exists()) {
    // ownerUsername is denormalized for routing — never persist a project without it.
    throw new ProjectError('not-found', 'User profile not found.');
  }
  const ownerUsername = userSnap.data().username;
  // Same snapshot that gave us ownerUsername, so the classroom stamp is free. Omitted (not '')
  // when the owner is in no classroom, so the field reads as genuinely unset.
  const classroomId = userSnap.data().currentClassroomId || '';
  const ref = doc(collection(db, 'projects'));
  await setDoc(ref, {
    name: input.name,
    userId: uid,
    ownerUsername,
    visibility: input.visibility ?? 'Private',
    editableByLink: false,
    files: input.files.map(toStoredFile),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastEditedBy: uid,
    lastEditedSession: CLIENT_SESSION_ID,
    ...(classroomId ? { classroomId } : {})
  });
  // Force a server read: a getDoc here can resolve from local cache (e.g. when a live onSnapshot
  // listener is attached) where the just-written serverTimestamp() is still pending and reads back
  // as null, collapsing updatedAt to 0 and poisoning the optimistic-concurrency token.
  const fresh = await getDocFromServer(ref);
  return toEditorProject(ref.id, fresh.data());
}

/** Read a project by owner username + id. Validates the username matches (mirrors the old route). */
export async function getProject(username: string, id: string) {
  const snap = await getDoc(doc(db, `projects/${id}`));
  if (!snap.exists() || snap.data().ownerUsername !== username) {
    throw new ProjectError('not-found', 'Project not found.');
  }
  return toEditorProject(id, snap.data());
}

/**
 * Subscribe to a project doc for live updates (instructor edits → student sees them). `onData` is
 * called with the editor-shaped project on the first snapshot and every subsequent change. Mirrors
 * the AR viewer's `subscribeSession`. Validates `ownerUsername` so a URL/username mismatch is treated
 * as not-found (same contract as `getProject`). Returns the unsubscribe function.
 */
export function subscribeProject(
  username: string,
  id: string,
  onData: (project: ReturnType<typeof toEditorProject>) => void,
  onError?: (err: Error) => void
): () => void {
  const ref = doc(db, `projects/${id}`);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists() || snap.data().ownerUsername !== username) return;
      onData(toEditorProject(id, snap.data()));
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

/**
 * Transactional save with an optimistic-concurrency precondition: reject a save built on a version
 * older than what's stored — BUT only when the newer stored version was written by a DIFFERENT tab
 * (a genuine "saved from another window"), identified by `lastEditedSession`. We can't use a pure
 * `updatedAt` comparison: the write sets `updatedAt: serverTimestamp()`, and the client SDK cannot
 * reliably read that resolved value back right after committing (with a live onSnapshot listener
 * attached, the read returns the pre-commit snapshot). So the client's known `updatedAt` perpetually
 * lags its own writes by one save; a strict comparison would flag every second save in a single tab
 * as stale. Gating on the per-tab session id lets this tab resave over its own earlier work while
 * still catching another tab or user (a second tab, or an instructor editing a student's sketch).
 * A doc with no `lastEditedSession` (legacy, or written by an older client) is treated as foreign, so
 * a concurrent legacy edit still trips the guard. Owner mismatch → 'forbidden'.
 * Also performs adopt-on-save for classroomId — see the inline comment.
 */
export async function updateProject(
  id: string,
  changes: { name?: string; files?: any[]; visibility?: 'Public' | 'Private' },
  knownUpdatedAt?: number,
  options: { classroomId?: string } = {}
) {
  const uid = requireUid();
  // Instructors/admins may save any student's sketch directly (Firestore rules permit staff writes).
  // Non-owner non-staff with a shared edit link save via the saveSharedSketch Cloud Function instead.
  const staff = isStaffRole(await getCurrentRole());
  const ref = doc(db, `projects/${id}`);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists())
      throw new ProjectError('not-found', 'Project not found.');
    const data = snap.data();
    if (data.userId !== uid && !staff)
      throw new ProjectError('forbidden', 'Not the owner.');
    if (
      knownUpdatedAt != null &&
      tsToMillis(data.updatedAt) > knownUpdatedAt &&
      data.lastEditedSession !== CLIENT_SESSION_ID
    ) {
      throw new ProjectError('stale', 'Stale version of project.');
    }
    const update: any = {
      updatedAt: serverTimestamp(),
      lastEditedBy: uid,
      lastEditedSession: CLIENT_SESSION_ID
    };
    if (changes.name !== undefined) update.name = changes.name;
    if (changes.visibility !== undefined)
      update.visibility = changes.visibility;
    if (changes.files !== undefined)
      update.files = changes.files.map(toStoredFile);
    // Adopt-on-save: a sketch made before this field existed (or before the student joined a
    // classroom) is stamped the first time its OWNER saves while in one. Three guards, each
    // load-bearing:
    //   !data.classroomId    — never rewrite an existing stamp; this preserves "created in".
    //   options.classroomId  — nothing to adopt into when the saver is in no classroom.
    //   data.userId === uid  — the important one. Staff may save a student's sketch directly;
    //                          without this, an instructor's save would stamp the student's
    //                          sketch with the INSTRUCTOR's classroom.
    // Costs no extra read: the transaction already fetched `data` for the stale-save precondition.
    if (!data.classroomId && options.classroomId && data.userId === uid) {
      update.classroomId = options.classroomId;
    }
    tx.update(ref, update);
  });
  // Read the fresh doc from the server (not cache): with a live onSnapshot listener attached, a plain
  // getDoc can resolve from local cache where a just-written serverTimestamp() is still pending and
  // reads back as null → updatedAt would collapse to 0. Note this value can still lag the timestamp we
  // just committed (see the precondition note above); the stale guard no longer depends on it.
  const fresh = await getDocFromServer(ref);
  return toEditorProject(id, fresh.data());
}

export async function changeVisibility(
  id: string,
  visibility: 'Public' | 'Private'
) {
  return updateProject(id, { visibility });
}

export async function deleteProject(id: string) {
  requireUid();
  try {
    await deleteDoc(doc(db, `projects/${id}`));
  } catch (error: any) {
    // Map the rules-enforced ownership failure to the typed contract the action layer expects.
    if (error?.code === 'permission-denied') {
      throw new ProjectError('forbidden', 'Not the owner.');
    }
    throw error;
  }
}

/** Clone: copy files (caller already re-generated ids + copied media) into a new doc. */
export async function cloneProject(input: { name: string; files: any[] }) {
  return createProject({ name: input.name, files: input.files });
}

/**
 * List the current user's own sketches WITH their files. Used to find and clean up references to a
 * deleted media asset. Owner-only (queries `userId == uid`), so the rules always permit it.
 */
export async function listOwnProjectsWithFiles(): Promise<
  Array<{ id: string; name: string; files: any[]; updatedAt: number }>
> {
  const uid = requireUid();
  const q = query(collection(db, 'projects'), where('userId', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      files: (data.files || []).map(fromStoredFile),
      updatedAt: tsToMillis(data.updatedAt)
    };
  });
}

/**
 * List a user's sketches. Own user (or `includePrivate`, granted to instructors/admins) → all;
 * any other user → only their Public sketches (rules reject a query that includes unreadable docs).
 * Ordered by updatedAt desc. `includePrivate` lets an instructor list a student's Private sketches
 * for View Classroom — the staff read rule permits the unfiltered query.
 * Pass options.classroomId to scope the result to the sketches created in that classroom (the
 * instructor-facing classroom listings); omit it for the owner's own dashboard, which stays
 * unscoped.
 */
export async function listProjects(
  username: string,
  includePrivate = false,
  options: { classroomId?: string } = {}
) {
  const mirror = await getDoc(doc(db, `usernames/${username}`));
  if (!mirror.exists())
    return { projects: [], metadata: { hasPagination: false } };
  const ownerUid = mirror.data().uid;
  const showAll = auth.currentUser?.uid === ownerUid || includePrivate;
  const base = collection(db, 'projects');
  const q = showAll
    ? query(base, where('userId', '==', ownerUid), orderBy('updatedAt', 'desc'))
    : query(
        base,
        where('userId', '==', ownerUid),
        where('visibility', '==', 'Public'),
        orderBy('updatedAt', 'desc')
      );
  const snap = await getDocs(q);
  // Classroom scoping (View classroom / Browse classroom / Publish classroom) filters CLIENT-SIDE
  // rather than adding where('classroomId','==',id) to the query: a second equality alongside the
  // orderBy('updatedAt') would need a new composite index per branch, and the query is already
  // bounded by userId ==, so a per-student list is small. Falsy classroomId = no scoping.
  const scopeTo = options.classroomId || '';
  const projects = snap.docs
    .filter((d) => !scopeTo || d.data().classroomId === scopeTo)
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name,
        visibility: data.visibility,
        createdAt: tsToMillis(data.createdAt),
        updatedAt: tsToMillis(data.updatedAt)
      };
    });
  return {
    projects,
    metadata: { hasPagination: false, totalProjects: projects.length }
  };
}
